import AudiobookSticker from '@backrs_ui/common-ui/src/assets/images/stickers/Audiobook.png';
import CalendarSticker from '@backrs_ui/common-ui/src/assets/images/stickers/Calendar.png';
import ChancesSticker from '@backrs_ui/common-ui/src/assets/images/stickers/Chances.png';
import ContactCardSticker from '@backrs_ui/common-ui/src/assets/images/stickers/ContactCard.png';
import CraftsDIYSticker from '@backrs_ui/common-ui/src/assets/images/stickers/CraftsDIY.png';
import DancingShoeSticker from '@backrs_ui/common-ui/src/assets/images/stickers/DancingShoes.png';
import DataChartSticker from '@backrs_ui/common-ui/src/assets/images/stickers/DataChart.png';
import DesignTechSticker from '@backrs_ui/common-ui/src/assets/images/stickers/DesignTech.png';
import FlagSticker from '@backrs_ui/common-ui/src/assets/images/stickers/Flag.png';
import KeySticker from '@backrs_ui/common-ui/src/assets/images/stickers/Key.png';
import MagnifyingGlassSticker from '@backrs_ui/common-ui/src/assets/images/stickers/MagnifyingGlass.png';
import MerchandiseSticker from '@backrs_ui/common-ui/src/assets/images/stickers/Merchandise.png';
import MicrophoneSticker from '@backrs_ui/common-ui/src/assets/images/stickers/Microphone.png';
import NewspaperSticker from '@backrs_ui/common-ui/src/assets/images/stickers/Newspaper.png';
import PodcastSticker from '@backrs_ui/common-ui/src/assets/images/stickers/Podcast.png';
import ResumeSticker from '@backrs_ui/common-ui/src/assets/images/stickers/Resume.png';
import SleepCloudSticker from '@backrs_ui/common-ui/src/assets/images/stickers/SleepCloud.png';
import StressThoughtSticker from '@backrs_ui/common-ui/src/assets/images/stickers/StressThought.png';
import StudyingSticker from '@backrs_ui/common-ui/src/assets/images/stickers/Studying.png';
import ThumbsUpSticker from '@backrs_ui/common-ui/src/assets/images/stickers/ThumbsUp.png';
import { AppIcons, AppIcon, ICON_SIZES } from '@backrs_ui/common-ui/src/designSystem/icons/AppIcon';
import { PanelNuggetActionParams } from '@backrs_ui/common-ui/src/models/nugget';

import { Box, Theme, SxProps, Typography } from '@mui/material';

import { BASE_NUGGET_IMAGE_MAX_HEIGHT } from '../BaseNugget';
import { HoverButton } from '../HoverButton';

type NuggetCareerPathCoverImageProps = Readonly<{
  variant:
    | {
        type: 'CAREER_PATH';
        name: string;
        careerPathIcon?: AppIcons;
        careerPathCategoryName?: string;
      }
    | {
        type: 'COMPETENCY';
        name: string;
        slug?: string;
      };

  actionParams?: PanelNuggetActionParams;
  isHovered?: boolean;
}>;

export const getCompetencySticker = (slug: string) => {
  switch (slug?.toLowerCase()) {
    // Academic competencies
    case 'academics-studying':
      return StudyingSticker;
    case 'academics-classes-majors-degrees':
      return AudiobookSticker;
    case 'foundational-learning':
      return CraftsDIYSticker;

    // Career competencies
    case 'careers-interviewing':
      return PodcastSticker;
    case 'careers-networking':
      return MicrophoneSticker;
    case 'careers-shadowing':
      return ContactCardSticker;
    case 'careers-resume':
      return ResumeSticker;
    case 'careers-social-media':
      return DesignTechSticker;

    // Mindset competencies
    case 'mindsets-growth-mindset':
      return ThumbsUpSticker;
    case 'foundational-problem-solving':
      return CalendarSticker;
    case 'mindsets-agency':
      return DancingShoeSticker;

    // Wellness competencies
    case 'wellness-stress-anxiety':
      return StressThoughtSticker;
    case 'wellness-sleep-restoration':
      return SleepCloudSticker;
    case 'wellness-family-dynamics':
      return MerchandiseSticker;
    case 'wellness-accessing-healthcare':
      return FlagSticker;
    case 'foundational-seeking-support':
      return MagnifyingGlassSticker;

    // Financial competencies
    case 'finances-credit-debt':
      return DataChartSticker;
    case 'finances-savings-investing':
      return ChancesSticker;
    case 'finances-securing-extra-cash':
      return NewspaperSticker;

    // Default fallback
    default:
      return AudiobookSticker;
  }
};

export const NuggetDefaultCoverImage = ({
  variant,
  actionParams,
  isHovered,
}: NuggetCareerPathCoverImageProps) => {
  const competencySticker =
    variant.type === 'COMPETENCY' && variant.slug ? getCompetencySticker(variant.slug) : KeySticker;

  const shouldUseAppIcon = variant.type === 'CAREER_PATH' && variant.careerPathIcon;
  const fallbackSticker = variant.type === 'COMPETENCY' ? competencySticker : KeySticker;

  return (
    <Box sx={getContainerSx(variant.type)}>
      <Box sx={contentWrapperSx}>
        <Box sx={stickerContainerSx(variant.type)}>
          {shouldUseAppIcon && variant.careerPathIcon ? (
            <AppIcon iconName={variant.careerPathIcon} diameter="32px" color="Colors/Base/Black" />
          ) : (
            <Box component="img" src={fallbackSticker} alt="Sticker" sx={stickerSx} />
          )}
        </Box>
        <Box sx={titleContainerSx}>
          <Typography variant="display-xs-semibold" sx={titleSx}>
            {variant.name}
          </Typography>

          {variant.type === 'CAREER_PATH' && variant.careerPathCategoryName && (
            <Typography variant="text-sm-medium">{variant.careerPathCategoryName}</Typography>
          )}
        </Box>
      </Box>

      {actionParams && (
        <HoverButton
          isVisible={isHovered ?? false}
          handleActionAddTask={actionParams?.handleActionAddTask}
          handleActionShare={actionParams?.handleActionShare}
          handleActionBookmark={actionParams?.handleActionBookmark}
          hasBookmarked={actionParams?.hasBookmarked}
        />
      )}
    </Box>
  );
};

const getContainerSx =
  (variantType: 'CAREER_PATH' | 'COMPETENCY'): SxProps<Theme> =>
  ({ backrs }) => ({
    width: '100%',
    minHeight: '0',
    aspectRatio: '16/9',
    maxHeight: BASE_NUGGET_IMAGE_MAX_HEIGHT,
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor:
      variantType === 'CAREER_PATH'
        ? backrs.colors['Colors/Background/bg-brand-primary']
        : backrs.colors['Warning/50'],
    overflow: 'hidden',
    position: 'relative',
    borderRadius: backrs.radius['radius-2xl'],
    border: `1px solid ${backrs.colors['Colors/Base/Black']}`,
  });

const contentWrapperSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  width: '100%',
  height: '100%',
  paddingX: backrs.spacing['spacing-2xl'],
});

const titleContainerSx: SxProps<Theme> = ({ backrs }) => ({
  width: '60%',
  flexShrink: 0,
  marginLeft: backrs.spacing['spacing-xl'],
  alignItems: 'center',
});

const titleSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Gray/900'],
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  display: '-webkit-box',
  WebkitLineClamp: 3,
  WebkitBoxOrient: 'vertical',
});

const stickerContainerSx =
  (variantType: 'CAREER_PATH' | 'COMPETENCY'): SxProps<Theme> =>
  ({ backrs }) => ({
    width: '40%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',

    ...(variantType === 'CAREER_PATH' && {
      width: ICON_SIZES['64px'],
      height: ICON_SIZES['64px'],
      backgroundColor: backrs.colors['Colors/Foreground/fg-brand-secondary'],
      border: `1px solid ${backrs.colors['Colors/Base/Black']}`,
      borderRadius: '50%',
    }),
  });

const stickerSx: SxProps<Theme> = {
  maxWidth: '100%',
  maxHeight: '100%',
  objectFit: 'contain',
};
